<template>
  <div class="patient-events" :class="{'patient-events--compact': isCompactMode}">
    <b-modal v-model="modalOpen" :component="getAlarmComponent()" :events="{}" :props="{ 'monitor_id': this.widgetModel.monitorId, existingData: existingData }" :can-cancel="false"></b-modal>
    <promised :promise="promise" v-if="!hasError">
      <template #pending>
        <patient-events-widget-skeleton></patient-events-widget-skeleton>
      </template>
      <template #default="{ data }">
        <ul v-if="data.length > 0">
          <li v-for="(item, index) in data" :key="index" class="patient-events__item" :class="{'open': isOpen(item), 'has-revisions': hasRevisions(item) }">
            <div v-if="data[index - 1] && item.payload.patient_monitor_id != data[index - 1].payload.patient_monitor_id" class="session-divider">
              <div class="session-divider__line"></div>
              <div class="session-divider__content">
                <p>Monitoring Session</p>
                <span>{{ $helpers.formatDate(item.payload.monitor_started_at, 'L') }} - {{ $helpers.formatDate(item.payload.monitor_ended_at, 'L') }}</span>
              </div>
              <div class="session-divider__line"></div>
            </div>
            <div class="patient-events__item-container" @click="toggleRevisions(item)">
              <button v-if="($can('Edit', 'StatAlarm') || (item.user_id == $store.state.currentUser.id)) && item.category == 'manual'" class="button is-primary edit-btn" @click="openEventEdit(item)">
                <span class="mdi mdi-pencil-outline"></span>
              </button>
              <component :is="`${item.type}-event`" :payload="item.payload" :class="{'has-revisions': hasRevisions(item), 'open': isOpen(item)}"></component>
              <p class="patient-events__date">
                <span>{{ $helpers.formatDate(item.updated_at) }}</span>
                <span v-if="item.category == 'manual' && item.audited && item.audited.length"> by {{ item.audited[item.audited.length - 1].username }}</span>
              </p>
              <div class="revisions" v-if="hasRevisions(item) && visibleRevision == item.payload.id">
                <ul>
                  <li :key="index" v-for="(revision, index) in $helpers.modifyRevisionsInPlace(item.audited)">
                    <stat-alarm-event hide-time-of-event :payload="revision" :categories="categories"></stat-alarm-event>
                    <p class="patient-events__date">{{ $helpers.formatDate(revision.edited_at) }} by {{ revision.username }}</p>
                  </li>
                </ul>
              </div>
            </div>
          </li>
        </ul>
        <p v-else class="no-data-message">There are no events to display</p>
      </template>
      <template v-slot:rejected="error">
        <p>Error: {{ error.message }}</p>
      </template>
    </promised>
    <div v-else class="widget-permission-error">{{ errorMessage }}</div>
  </div>
</template>

<script>
import PatientEventsWidgetSkeleton from "@/components/skeletons/PatientEventsWidgetSkeleton";
import WidgetMixin from "@/components/mixins/WidgetMixin";
import StatAlarmEvent from "@/components/patient_events/StatAlarmEvent";
import AiEvent from "@/components/patient_events/AiEvent";
import GenericWidgetGuard from "@/components/permissions/GenericWidgetGuard";
import Alarm from "@/components/Alarm";
import to from "@/lib/to";

export default {
  name: "PatientEventsWidget",
  components: {PatientEventsWidgetSkeleton, StatAlarmEvent, AiEvent, Alarm},
  mixins: [WidgetMixin],
  widget: {
    serviceMethod: 'getEvents',
    guard: GenericWidgetGuard,
    title: 'Patient Events',
    actions: [
      {
        isSection: true,
        isClickable: false,
        title: 'Filters',
      },
      {
        type: 'button-group',
        isClickable: false,
        init(buttons) {
          buttons.model = this.widgetModel.flexView ? 'all' : this.widgetModel.params.filter
        },
        buttons: [
          { title: 'AI', value: 'ai' }, { title: 'Manual', value: 'manual' }, { title: 'All', value: 'all' }
        ],
        handler(el) {
          const selectedValue = el.target.value
          this.widgetModel.params.filter = selectedValue
          this.performRequest()
          if (!this.widgetModel.flexView) {
            this.updateWidget({
              params: {
                filter: selectedValue
              }
            })
          }
        }
      }
    ]
  },
  watch: {
    'widgetModel.params.filter_monitoring': function (value) {
      this.widgetModel.params.filter = value
      this.performRequest()
    }
  },
  computed: {
    isCompactMode() {
      return this.widgetModel.settings.compactMode || this.widgetModel.flexView
    }
  },
  methods: {
    getAlarmComponent() {
      return Alarm
    },
    openEventEdit(event) {
      const ev = {...event}
      ev.payload = {...ev.payload}
      this.existingData = ev
      this.modalOpen = true
    },
    toggleRevisions(item) {
      if (!this.hasRevisions(item)) { return true }
      const id = item.payload.id
      this.visibleRevision = this.visibleRevision == id ? null : id
    },
    hasRevisions(item) {
      return item.audited && item.audited.length > 1
    },
    isOpen(item) {
      return this.visibleRevision && this.visibleRevision == item.payload.id
    },
    patientEventsUpdatedHandler({ monitor_id }) {
      if (this.widgetModel.monitorId == monitor_id) {
        this.performRequest()
      }
    }
  },
  async mounted() {
    const channelName = `private-encrypted-ld-${import.meta.env.VITE_ENVIRONMENT_NAME}-hospital-${this.$store.state.currentUser.hospital_id}`
    this.pusherChannel = this.$pusher.subscribe(channelName)
    this.pusherChannel.bind('patient-events-updated', this.patientEventsUpdatedHandler);
    const [err, response] = await to(this.$http.get('/stat_alarms/categories', { skipLoading: true }))
    if (!err) {
      this.categories = response.data
    }
  },
  beforeUnmount() {
    this.pusherChannel?.unbind('patient-events-updated', this.patientEventsUpdatedHandler)
  },
  data() {
    return {
      categories: [],
      existingData: null,
      modalOpen: false,
      visibleRevision: null,
      pusherChannel: null
    }
  }
}
</script>

<style scoped lang="scss">
  .patient-events__item {
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.1s linear;
    > .patient-events__item-container {
      display: flex;
      flex-direction: column;
      padding-right: 5px;
      position: relative;
    }
    &.open {
      background: #f3f5fb;
      border-radius: 8px;
      padding: 10px;
      box-shadow: 1px 1px 3px 0px #cacaca;
      margin-bottom: 15px !important;
    }
    &:hover .edit-btn {
      display: block;
    }
    &.has-revisions {
      cursor: pointer;
      .patient-events__date {
        margin-top: 8px;
      }
      .edit-btn {
        right: 0;
      }
    }

    &:not(:last-child) {
      margin-bottom: 15px;
    }
    :deep(span) {
      font-size: 13px;
      line-height: 18px;
      &.key {
        color: $secondary-text-color;
        font-size: 11px;
        text-transform: uppercase;
        font-weight: 600;
        letter-spacing: 1px;
        padding-right: 5px;
      }
    }
  }
  :deep() {
    .patient-events__item-content {
      border-radius: 20px;
      padding: 15px 10px 12px;
      background: $background-level-0;
    }
    .patient-events__date {
      margin: 5px 5px 0 auto;
      font-size: 12px;
      opacity: 0.7;
    }
  }

  .patient-events--compact {
    :deep() {
      .patient-events__item {
        span {
          font-size: 12px;
          line-height: 13px;
          &.key {
            font-size: 10px;
          }
        }
        .patient-events__item-content {
          border-radius: 8px;
          padding: 5px 10px;
        }
        &:not(:last-child) {
          margin-bottom: 5px;
        }
      }
    }
    .patient-events__date {
      font-size: 10px;
    }
  }
  .edit-btn {
    display: none;
    position: absolute;
    border-radius: 0 20px 0 10px;
    height: 27px;
    width: 32px;
    padding: 0;
    right: 5px;
    top: 0px;
    .patient-events--compact & {
      border-radius: 0 8px 0 10px;
      height: 25px;
    }
  }
  .revisions {
    margin-top: 10px;
    ul li {
      margin-left: 20px;
      display: flex;
      flex-direction: column;
      &:not(:last-child) {
        margin-bottom: 10px;
      }
    }
  }
  .session-divider {
    display: flex;
    align-items: center;
    margin: 15px 0 25px;
    .session-divider__line {
      background: #cdcfd7;
      height: 1px;
      flex: 1;
    }
    .session-divider__content {
      display: flex;
      flex-direction: column;
      padding: 0 10px;
      align-items: center;
      p {
        font-size: 11px;
        text-transform: uppercase;
        font-weight: 500;
        letter-spacing: 1px;
        padding-bottom: 3px;
      }
    }
  }
</style>
